/**
 * Compact KPI tile for dashboard customize widgets (MetricCard type).
 */

import { useQuery } from '@tanstack/react-query';
import { EmptyState, LoadingSkeleton } from '@coordio/ui/layout';

import { dashboardService } from '../../services/dashboardService';
import { useContextStore } from '../../store/contextStore';
import type { DashboardMetricsDto } from '../../types/dashboard';

import './dashboardWidgets.scss';

type MetricCardWidgetProps = {
  metric?: string;
};

const METRIC_LABELS: Record<string, string> = {
  activeAgents: 'Active agents',
  activeSessions: 'Active sessions',
  totalExecutionsToday: 'Executions today',
  totalTokensToday: 'Tokens today',
  totalCostToday: 'Cost today',
  totalApiCallsToday: 'API calls today',
};

const formatMetric = (key: string, metrics: DashboardMetricsDto): string => {
  switch (key) {
    case 'activeAgents':
      return metrics.activeAgents.toLocaleString();
    case 'activeSessions':
      return metrics.activeSessions.toLocaleString();
    case 'totalExecutionsToday':
      return metrics.totalExecutionsToday.toLocaleString();
    case 'totalTokensToday':
      return metrics.totalTokensToday.toLocaleString();
    case 'totalCostToday':
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(metrics.totalCostToday);
    case 'totalApiCallsToday':
      return metrics.totalApiCallsToday.toLocaleString();
    default:
      return '—';
  }
};

export const MetricCardWidget = ({ metric = 'activeSessions' }: MetricCardWidgetProps) => {
  const tenantId = useContextStore((s) => s.currentTenant?.id);
  const projectId = useContextStore((s) => s.currentProject?.id);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['dashboard', 'mainMetrics', tenantId, projectId],
    queryFn: () => dashboardService.getMainMetrics(),
    enabled: !!tenantId,
  });

  if (isLoading) return <LoadingSkeleton rows={2} />;
  if (isError || !data) {
    return <EmptyState title="Metric unavailable" description="Could not load the selected KPI." />;
  }

  const label = METRIC_LABELS[metric] ?? metric;

  return (
    <div className="metric-tile" data-testid="metric-card-widget">
      <span className="metric-tile__label">{label}</span>
      <span className="metric-tile__value">{formatMetric(metric, data)}</span>
    </div>
  );
};
